/**
 * Who tells the loading screen what the boot is doing.
 *
 * Two rules live here, and they are here rather than inline in `scene-boot.ts`
 * because each is a claim about the boot's own record that a test should be able
 * to hold to on a machine with no GPU:
 *
 * 1. **One `performance.mark` per phase, at the phase's start.** The whole of
 *    `docs/engineering/fly-boot-timing.md` is read off those marks, and a byte
 *    report is not a phase starting — it is the phase already running saying
 *    more about itself. A second mark for the same name would put a duration of
 *    zero in that table.
 * 2. **A download reports under its own phase's label or not at all.** The
 *    boot's three fetches are started together and awaited in order
 *    (`scene-boot.ts`), so the airframe's bytes begin arriving while the label
 *    still says `LOADING THE GUNS`. Forwarding them then would jump the bar into
 *    the airframe's segment under the wrong caption. They are held instead, and
 *    the phase announcement carries whatever has arrived by the time it happens.
 *
 * Nothing here imports three.js, for the same reason `boot-progress.ts` does
 * not: the boot's *bookkeeping* should be testable on a machine with no adapter.
 */

import { bootProgressFor, type BootBytes, type BootPhase, type BootProgress } from "./boot-progress.ts";

/** The one callback this needs from `FlightSceneCallbacks`, named structurally so no scene type is imported. */
export interface BootProgressSink {
  readonly onBootProgress?: (progress: BootProgress) => void;
}

export interface BootReporter {
  /** The boot has moved on to `phase`: mark it once, and report it with whatever its download has already said. */
  phase(phase: BootPhase): void;
  /** Fresh byte counts for `phase`'s own download. Silent until `phase` has been announced. */
  bytes(phase: BootPhase, loaded: number, total: number): void;
  /**
   * A standing fact to print under the bar for the rest of the boot — see
   * `BootProgress.note`. Sticky rather than a one-shot report, because the
   * thing it says (`RENDERER: WEBGL`) is true of every phase after it, and a
   * caption that vanished on the next phase would be a flicker nobody could
   * read. Re-reports the phase already running so the line appears at once.
   */
  note(text: string): void;
}

export function createBootReporter(callbacks: BootProgressSink): BootReporter {
  const marked = new Set<BootPhase>();
  const counts = new Map<BootPhase, BootBytes>();
  let standing: string | undefined;
  let current: BootPhase | undefined;
  const report = (phase: BootPhase): void => {
    current = phase;
    callbacks.onBootProgress?.(bootProgressFor(phase, counts.get(phase), standing));
  };
  return {
    phase: (phase) => {
      if (!marked.has(phase)) {
        marked.add(phase);
        performance.mark(`fly-boot:${phase}`);
      }
      report(phase);
    },
    bytes: (phase, loaded, total) => {
      counts.set(phase, { loaded, total });
      if (marked.has(phase)) report(phase);
    },
    note: (text) => {
      standing = text;
      // Silent before the first phase — there is no report to attach a note to
      // yet, and the phase announcement that follows will carry it.
      if (current !== undefined) report(current);
    },
  };
}
